// Ritual Consultation offering page copy (EN + NL).
export const RITUAL = {
  en: {
    seo: {
      title: "Ritual Consultation | Women's Den",
      description: "Personal rituals co-created with you to mark menarche, motherhood, menopause and other meaningful life transitions. Ritual Consultation with Irida Hysenbelli.",
    },
    label: "Ritual Consultation",
    h1: "Something You Would Like to Mark?",
    opening: [
      "There are moments in life that carry meaning, yet pass without being fully acknowledged.",
      "A daughter begins menstruating. A woman becomes a mother. Breastfeeding comes to an end. Menopause arrives. A relationship, identity or period of life is left behind. Something new begins.",
      "Sometimes we know that a moment matters before we know how to meet it.",
      "A Ritual Consultation offers space to listen to what this transition means and explore how it might be marked in a way that feels personal, grounded and true.",
    ],
    cta: "Begin with a conversation",
    giving: {
      title: "Giving a Transition a Place",
      paras: [
        "Not every important change comes with an inherited way of acknowledging it. Even where traditions exist, they may not reflect the person we are, the life we have lived or what this particular moment means to us.",
        "A ritual gives a transition a place. It can help us recognise what has happened, honour what has been carried, acknowledge what is ending and turn consciously towards what is beginning.",
        "The ritual may be private. It may be shared between two people or witnessed by a small group. It might involve words, silence, an object, a symbolic action, food, movement or time together.",
        "Its meaning does not depend on being elaborate. It comes from expressing something that matters with honesty and attention.",
      ],
    },
    create: {
      title: "How We Create the Ritual",
      quote: "A personal ritual, co-created with you and grounded in your own experience, language and relationships.",
      paras: [
        "I do not begin with a ready-made ceremony or decide what your ritual should look like.",
        "We begin with your experience: what is changing, why it matters now, what you want to honour and what you hope the ritual might hold.",
        "I bring careful listening, questions, ritual knowledge and practical guidance. You bring the meaning, relationships, memories and language of your own life. We allow the intention and form of the ritual to emerge through the process together.",
        "Every element should have a reason for being there. Nothing is included simply because it appears ceremonial.",
      ],
    },
    brings: {
      title: "What Brings People to This Work?",
      intro: "You may wish to acknowledge:",
      items: [
        "a daughter's menarche",
        "becoming a mother",
        "the end of breastfeeding",
        "entering menopause",
        "a significant birthday or age threshold",
        "recovery or return",
        "loss, separation or letting go",
        "a change in identity or relationship",
        "the beginning of a new chapter",
        "another transition that carries personal meaning",
      ],
      close: "You do not need to arrive knowing what the ritual should be. Discovering what wants to be acknowledged is part of the consultation.",
    },
    begin: {
      title: "How We Begin",
      steps: [
        {
          title: "An introductory conversation",
          paras: ["We begin with a free 15–20-minute call. You can tell me a little about what you would like to mark, and we can consider whether my approach feels appropriate for you."],
        },
        {
          title: "Preparation questions",
          paras: [
            "Once you decide to continue, I send you a small collection of reflective questions. Your responses help bring some of the important threads into view before we meet.",
            "You do not need to write extensively or have everything worked out. A few honest sentences are enough. You may also respond through a voice recording if speaking feels easier.",
          ],
        },
        {
          title: "The Ritual Consultation",
          paras: [
            "During the consultation, we explore the transition, its meaning and what you would like the ritual to hold.",
            "Only then do we begin considering its form: who might be involved, where it could take place, what might be spoken and whether particular objects, gestures or traditions belong.",
          ],
        },
        {
          title: "What follows",
          paras: ["What happens after the consultation depends on the form of support you choose. You may leave with greater clarity, receive a written guide for a ritual you will hold yourself, or ask me to remain involved and facilitate the ritual with you."],
        },
      ],
    },
    ways: {
      title: "Ways to Work Together",
      options: [
        {
          key: "explore",
          name: "Exploring the Threshold",
          price: "€125",
          paras: [
            "A 90-minute consultation for someone who feels that an experience or transition deserves acknowledgement and wants space to understand it more clearly.",
            "Before we meet, you receive reflective preparation questions. During the consultation, we explore what is changing, what you want to honour and whether a ritual feels appropriate.",
            "You leave with a clearer understanding of the ritual's possible intention and form. You can then continue developing or holding it yourself.",
          ],
          includesTitle: "Includes",
          includes: ["preparation questions", "my preparation from your responses", "one 90-minute Ritual Consultation"],
          cta: "Book a consultation",
        },
        {
          key: "create-self",
          name: "Your Personal Ritual",
          price: "€245",
          emphasised: true,
          paras: [
            "For someone who would like support to co-create a personal ritual that she will hold herself.",
            "The process includes preparation questions and a 90-minute Ritual Consultation. After our conversation, I bring together what we have created in a written Personal Ritual Guide.",
            "The guide sets out the intention, preparation, participants and sequence of the ritual, with suggested words or symbolic actions where these are helpful.",
            "A 30-minute follow-up gives us time to review the guide, clarify anything that remains uncertain and make small refinements.",
          ],
          includesTitle: "Includes",
          includes: [
            "preparation questions",
            "my preparation from your responses",
            "one 90-minute Ritual Consultation",
            "one written Personal Ritual Guide",
            "one 30-minute follow-up conversation",
            "small refinements agreed during the follow-up",
          ],
          note: "This option does not include my presence at the ritual, arranging a venue, sourcing materials or coordinating participants.",
          cta: "Create a personal ritual",
        },
        {
          key: "facilitate",
          name: "A Ritual Held With You",
          price: "From €500",
          paras: [
            "For someone who would like us to co-create the ritual and would also like me to be present to hold and facilitate it.",
            "We develop the ritual through the preparation and consultation process. I then hold its structure on the day: welcoming and orienting those present, guiding the sequence, protecting the intention and adapting where needed.",
            "This allows you to remain within the experience rather than managing the gathering or remembering what needs to happen next.",
          ],
          includesTitle: "The process includes",
          includes: [
            "preparation questions",
            "my preparation from your responses",
            "one 90-minute Ritual Consultation",
            "co-creation of the ritual",
            "an agreed ritual outline",
            "phone and message guidance during the preparation",
            "my presence and facilitation on the day",
            "a brief follow-up afterwards",
          ],
          note: "Each ritual asks for a different level of involvement. The final fee depends on what we agree together, including the number of people involved, location, duration, preparation, materials and level of coordination required.",
          note2: "Venue hire, substantial materials, significant travel and any additional practitioners are quoted separately. The complete scope and fee are agreed before further preparation begins.",
          cta: "Tell me what you would like to create",
        },
      ],
    },
    menarche: {
      title: "When a Mother Wants to Mark Her Daughter's Menarche",
      paras: [
        "A mother may wish to offer her daughter something different from what she herself received at menarche.",
        "The consultation creates space to explore that wish while keeping the daughter's experience central. Her comfort, preferences and consent guide every decision.",
        "A meaningful acknowledgement might be a small gathering. It might equally be a private conversation, a shared meal, a gift, time together or a simple gesture understood only between mother and daughter.",
        "The purpose is not to make the experience more public than the daughter wants. It is to help the mother listen carefully and create something her daughter can genuinely receive.",
      ],
      listTitle: "Where a ritual involves a child or young person:",
      items: [
        "she remains involved in deciding what happens",
        "she may change her mind about any element",
        "she is never required to speak or share personal feelings",
        "her agreement is needed for photographs or recordings",
        "a parent or responsible adult remains involved throughout",
      ],
    },
    care: {
      title: "A Carefully Held Process",
      paras: [
        "Participation in any ritual remains voluntary. No one is required to speak, share private experiences or respond in a particular way.",
        "A ritual can be changed, simplified or paused. The person whose transition is being marked remains central throughout the process.",
        "Ritual Consultation offers reflective and creative support around a life transition. It does not replace medical, psychological, legal, religious or safeguarding support where this is needed.",
      ],
    },
    final: {
      title: "Begin With a Conversation",
      paras: [
        "If there is something you would like to mark, you are welcome to tell me a little about it.",
        "We begin with a free 15–20-minute conversation. This gives us an opportunity to understand what you are seeking, consider whether my approach is suitable and decide which form of support would serve you best.",
      ],
    },
    form: {
      heading: "Tell me what you would like to mark",
      name: "Name",
      email: "Email address",
      phone: "Telephone number (optional)",
      transition: "What transition or moment would you like to mark?",
      ritualFor: "Who is the ritual for?",
      datePeriod: "Is there a particular date or time period connected to it? (optional)",
      support: "Which form of support are you currently considering?",
      supportOptions: [
        { key: "explore", label: "I would like to explore the transition" },
        { key: "create-self", label: "I would like help creating a ritual to hold myself" },
        { key: "facilitate", label: "I may want Irida to facilitate the ritual" },
        { key: "unsure", label: "I am not yet sure" },
      ],
      extra: "Is there anything else you would like me to know before we speak?",
      consent: "I understand that submitting this form is an enquiry and does not confirm a booking.",
      invalid: "Please complete the required fields and confirm the enquiry note.",
      submit: "Request an introductory conversation",
      sending: "Sending…",
      error: "Something went wrong — please try again in a moment.",
      success: "Thank you for sharing what you would like to mark. You can now choose a time for a free 15-minute introductory conversation.",
      scheduleCta: "Schedule the introductory conversation",
    },
  },
  nl: {
    seo: {
      title: "Ritueel consult | Women's Den",
      description: "Persoonlijke rituelen, samen met jou gecreëerd om menarche, moederschap, menopauze en andere betekenisvolle levensovergangen te markeren. Ritueel consult met Irida Hysenbelli.",
    },
    label: "Ritueel consult",
    h1: "Iets dat je graag wilt markeren?",
    opening: [
      "Er zijn momenten in het leven die betekenis dragen, maar toch voorbijgaan zonder echt erkend te worden.",
      "Een dochter begint te menstrueren. Een vrouw wordt moeder. De borstvoeding komt ten einde. De menopauze kondigt zich aan. Een relatie, identiteit of levensfase wordt achtergelaten. Iets nieuws begint.",
      "Soms weten we dat een moment ertoe doet, voordat we weten hoe we ermee om kunnen gaan.",
      "Een ritueel consult biedt ruimte om te luisteren naar wat deze overgang betekent en te verkennen hoe hij gemarkeerd zou kunnen worden — op een manier die persoonlijk, geaard en waar aanvoelt.",
    ],
    cta: "Begin met een gesprek",
    giving: {
      title: "Een overgang een plek geven",
      paras: [
        "Niet elke belangrijke verandering komt met een overgeleverde manier om haar te erkennen. En waar tradities bestaan, passen ze misschien niet bij wie we zijn, het leven dat we hebben geleefd, of wat dit moment voor ons betekent.",
        "Een ritueel geeft een overgang een plek. Het kan ons helpen te erkennen wat er is gebeurd, te eren wat er is gedragen, te benoemen wat er eindigt en ons bewust te richten op wat er begint.",
        "Het ritueel kan besloten zijn. Het kan gedeeld worden tussen twee mensen, of in het bijzijn van een kleine groep. Het kan woorden bevatten, stilte, een voorwerp, een symbolische handeling, eten, beweging of tijd samen.",
        "De betekenis ervan hangt niet af van grootsheid. Hij ontstaat uit het met eerlijkheid en aandacht uitdrukken van iets dat ertoe doet.",
      ],
    },
    create: {
      title: "Hoe we het ritueel samen vormgeven",
      quote: "Een persoonlijk ritueel, samen met jou gecreëerd en geaard in jouw eigen ervaring, taal en relaties.",
      paras: [
        "Ik begin niet met een kant-en-klare ceremonie en bepaal niet hoe jouw ritueel eruit moet zien.",
        "We beginnen bij jouw ervaring: wat er verandert, waarom het nu ertoe doet, wat je wilt eren en wat je hoopt dat het ritueel kan dragen.",
        "Ik breng zorgvuldig luisteren, vragen, kennis van rituelen en praktische begeleiding mee. Jij brengt de betekenis, relaties, herinneringen en taal van je eigen leven mee. We laten de intentie en vorm van het ritueel samen door het proces ontstaan.",
        "Elk element moet een reden hebben om er te zijn. Niets wordt toegevoegd alleen omdat het er plechtig uitziet.",
      ],
    },
    brings: {
      title: "Wat brengt mensen naar dit werk?",
      intro: "Misschien wil je iets erkennen of markeren:",
      items: [
        "de menarche van een dochter",
        "moeder worden",
        "het einde van de borstvoeding",
        "het begin van de menopauze",
        "een bijzondere verjaardag of leeftijdsgrens",
        "herstel of terugkeer",
        "verlies, afscheid of loslaten",
        "een verandering in identiteit of relatie",
        "het begin van een nieuw hoofdstuk",
        "een andere overgang die persoonlijke betekenis draagt",
      ],
      close: "Je hoeft niet te weten hoe het ritueel eruit moet zien. Ontdekken wat erkend wil worden is deel van het consult.",
    },
    begin: {
      title: "Hoe we beginnen",
      steps: [
        {
          title: "Een kennismakingsgesprek",
          paras: ["We beginnen met een gratis gesprek van 15–20 minuten. Je kunt me wat vertellen over wat je wilt markeren, en we kunnen samen voelen of mijn manier van werken bij je past."],
        },
        {
          title: "Voorbereidende vragen",
          paras: [
            "Als je besluit door te gaan, stuur ik je een kleine verzameling reflectievragen. Je antwoorden helpen om voor ons gesprek al een paar belangrijke draden zichtbaar te maken.",
            "Je hoeft niet uitgebreid te schrijven of alles al uitgedacht te hebben. Een paar eerlijke zinnen zijn genoeg. Je mag ook een spraakbericht sturen als praten makkelijker voelt.",
          ],
        },
        {
          title: "Het ritueel consult",
          paras: [
            "Tijdens het consult verkennen we de overgang, de betekenis ervan en wat je wilt dat het ritueel draagt.",
            "Pas daarna kijken we naar de vorm: wie erbij betrokken zouden kunnen zijn, waar het zou kunnen plaatsvinden, wat er gezegd zou kunnen worden en welke voorwerpen, gebaren of tradities er horen.",
          ],
        },
        {
          title: "Wat er volgt",
          paras: ["Wat er na het consult gebeurt, hangt af van de vorm van begeleiding die je kiest. Je gaat misschien weg met meer helderheid, ontvangt een schriftelijke gids voor een ritueel dat je zelf houdt, of vraagt me betrokken te blijven en het ritueel samen met jou te begeleiden."],
        },
      ],
    },
    ways: {
      title: "Manieren om samen te werken",
      options: [
        {
          key: "explore",
          name: "De drempel verkennen",
          price: "€125",
          paras: [
            "Een consult van 90 minuten voor iemand die voelt dat een ervaring of overgang erkenning verdient en ruimte wil om die helderder te begrijpen.",
            "Voorafgaand ontvang je reflecterende voorbereidingsvragen. Tijdens het consult verkennen we wat er verandert, wat je wilt eren en of een ritueel passend voelt.",
            "Je gaat weg met een helderder beeld van de mogelijke intentie en vorm van het ritueel. Daarna kun je het zelf verder ontwikkelen of houden.",
          ],
          includesTitle: "Inbegrepen",
          includes: ["voorbereidende vragen", "mijn voorbereiding op basis van jouw antwoorden", "één ritueel consult van 90 minuten"],
          cta: "Vraag een consult aan",
        },
        {
          key: "create-self",
          name: "Jouw persoonlijke ritueel",
          price: "€245",
          emphasised: true,
          paras: [
            "Voor iemand die begeleiding wil bij het samen creëren van een persoonlijk ritueel dat ze zelf zal houden.",
            "Het proces omvat voorbereidende vragen en een ritueel consult van 90 minuten. Na ons gesprek breng ik wat we samen hebben gemaakt bijeen in een schriftelijke Persoonlijke Ritueelgids.",
            "De gids beschrijft de intentie, de voorbereiding, de deelnemers en het verloop van het ritueel, met voorgestelde woorden of symbolische handelingen waar die helpend zijn.",
            "Een vervolggesprek van 30 minuten geeft ons tijd om de gids door te nemen, te verduidelijken wat onzeker blijft en kleine verfijningen aan te brengen.",
          ],
          includesTitle: "Inbegrepen",
          includes: [
            "voorbereidende vragen",
            "mijn voorbereiding op basis van jouw antwoorden",
            "één ritueel consult van 90 minuten",
            "één schriftelijke Persoonlijke Ritueelgids",
            "één vervolggesprek van 30 minuten",
            "kleine verfijningen die we tijdens het vervolggesprek afspreken",
          ],
          note: "Deze vorm omvat niet mijn aanwezigheid bij het ritueel, het regelen van een locatie, het verzorgen van materialen of het coördineren van deelnemers.",
          cta: "Creëer een persoonlijk ritueel",
        },
        {
          key: "facilitate",
          name: "Een ritueel, samen met jou gehouden",
          price: "Vanaf €500",
          paras: [
            "Voor iemand die het ritueel samen wil creëren en ook wil dat ik aanwezig ben om het te dragen en te begeleiden.",
            "We ontwikkelen het ritueel via het voorbereidings- en consultproces. Op de dag zelf draag ik de structuur: de aanwezigen verwelkomen en oriënteren, het verloop begeleiden, de intentie bewaken en aanpassen waar nodig.",
            "Zo kun je in de ervaring blijven, in plaats van de bijeenkomst te regelen of te onthouden wat er daarna moet gebeuren.",
          ],
          includesTitle: "Het proces omvat",
          includes: [
            "voorbereidende vragen",
            "mijn voorbereiding op basis van jouw antwoorden",
            "één ritueel consult van 90 minuten",
            "het samen creëren van het ritueel",
            "een afgesproken opbouw van het ritueel",
            "begeleiding per telefoon en berichten tijdens de voorbereiding",
            "mijn aanwezigheid en begeleiding op de dag",
            "een kort vervolgcontact daarna",
          ],
          note: "Elk ritueel vraagt om een andere mate van betrokkenheid. Het uiteindelijke tarief hangt af van wat we samen afspreken, waaronder het aantal betrokkenen, de locatie, de duur, de voorbereiding, de materialen en de benodigde coördinatie.",
          note2: "Locatiehuur, omvangrijke materialen, aanzienlijke reistijd en eventuele andere begeleiders worden apart begroot. De volledige omvang en het tarief worden afgesproken voordat de verdere voorbereiding begint.",
          cta: "Vertel me wat je wilt creëren",
        },
      ],
    },
    menarche: {
      title: "Wanneer een moeder de menarche van haar dochter wil markeren",
      paras: [
        "Een moeder wil haar dochter misschien iets anders bieden dan wat zij zelf bij haar menarche ontving.",
        "Het consult biedt ruimte om die wens te verkennen, terwijl de ervaring van de dochter centraal blijft. Haar comfort, voorkeuren en instemming bepalen elke beslissing.",
        "Een betekenisvolle markering kan een kleine bijeenkomst zijn. Het kan net zo goed een persoonlijk gesprek zijn, een gedeelde maaltijd, een geschenk, tijd samen, of een eenvoudig gebaar dat alleen moeder en dochter begrijpen.",
        "Het doel is niet om de ervaring openbaarder te maken dan de dochter wil. Het is om de moeder te helpen zorgvuldig te luisteren en iets te creëren dat haar dochter echt kan ontvangen.",
      ],
      listTitle: "Wanneer een ritueel een kind of jongere betreft:",
      items: [
        "blijft ze betrokken bij de beslissing over wat er gebeurt",
        "mag ze over elk element van gedachten veranderen",
        "wordt ze nooit verplicht te spreken of persoonlijke gevoelens te delen",
        "is haar toestemming nodig voor foto's of opnames",
        "blijft een ouder of verantwoordelijke volwassene bij alles betrokken",
      ],
    },
    care: {
      title: "Een zorgvuldig gedragen proces",
      paras: [
        "Deelname aan een ritueel blijft altijd vrijwillig. Niemand is verplicht te spreken, persoonlijke ervaringen te delen of op een bepaalde manier te reageren.",
        "Een ritueel kan worden aangepast, vereenvoudigd of gepauzeerd. De persoon wiens overgang wordt gemarkeerd, blijft tijdens het hele proces centraal staan.",
        "Een ritueel consult biedt reflectieve en creatieve ondersteuning rond een levensovergang. Het vervangt geen medische, psychologische, juridische, religieuze of beschermende zorg waar die nodig is.",
      ],
    },
    final: {
      title: "Begin met een gesprek",
      paras: [
        "Als er iets is dat je graag wilt markeren, vertel me er gerust een beetje over.",
        "We beginnen met een gratis gesprek van 15–20 minuten. Zo kunnen we begrijpen wat je zoekt, afwegen of mijn manier van werken passend is en bepalen welke vorm van begeleiding je het beste zou dienen.",
      ],
    },
    form: {
      heading: "Vertel me wat je zou willen markeren",
      name: "Naam",
      email: "E-mailadres",
      phone: "Telefoonnummer (optioneel)",
      transition: "Welke overgang of welk moment zou je willen markeren?",
      ritualFor: "Voor wie is het ritueel?",
      datePeriod: "Is er een bepaalde datum of periode aan verbonden? (optioneel)",
      support: "Welke vorm van begeleiding overweeg je op dit moment?",
      supportOptions: [
        { key: "explore", label: "Ik wil de overgang graag verkennen" },
        { key: "create-self", label: "Ik wil hulp bij het creëren van een ritueel dat ik zelf houd" },
        { key: "facilitate", label: "Misschien wil ik dat Irida het ritueel begeleidt" },
        { key: "unsure", label: "Ik weet het nog niet zeker" },
      ],
      extra: "Is er nog iets dat je wilt dat ik weet voordat we spreken?",
      consent: "Ik begrijp dat het versturen van dit formulier een aanvraag is en geen boeking bevestigt.",
      invalid: "Vul de verplichte velden in en bevestig de opmerking over de aanvraag.",
      submit: "Vraag een kennismakingsgesprek aan",
      sending: "Versturen…",
      error: "Er ging iets mis — probeer het over een moment opnieuw.",
      success: "Dank je voor het delen van wat je wilt markeren. Je kunt nu een moment kiezen voor een gratis kennismakingsgesprek van 15 minuten.",
      scheduleCta: "Plan het kennismakingsgesprek",
    },
  },
};
